import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { parseISO, differenceInMinutes } from "date-fns";
import type { LucideIcon } from "lucide-react";
import {
  CalendarDays,
  Check,
  CheckCircle2,
  Clock,
  MessageCircle,
  ShieldCheck,
  Target,
  Users,
  Shield,
  Video,
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { publicApi } from "@/services/publicApi";
import type { PublicProgramPreviewOut } from "@/services/api";
import { ThemeToggle } from "@/components/ThemeToggle";
import achieveosLogo from "@/images/achieveos-logo.png";
import achieveosLogoDark from "@/images/achieveos-logo-dark.png";
import { FormattedText } from "@/components/FormattedText";
import { Button } from "@/components/Button";
import { tracking } from "@/utils/tracking";
import { FeaturedCard } from "@/pages/home/components/get-started/FeaturedProgramCarousel";
import { formatProgramDuration, publicDurationFields } from "@/utils/programDuration";
import { formatDisplayDate } from "@/utils/formatDisplayDate";

// ── Badge ──────────────────────────────────────────────────────────────────────

const Badge = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <span
    className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${className}`}
  >
    {children}
  </span>
);

// ── Detail card — matches Figma nodes 177:3411 / 177:3529 ──────────────────────
// --prog-tips-card-bg / --prog-card-border-color are the closest existing
// theme-aware tokens to those nodes' translucent-white card treatment (unlike
// --prog-card-bg, which resolves to a solid surface color and reads flatter
// than the reference — see tokens.css for both modes' values).

const DetailCard = ({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <div
    className={`rounded-2xl border border-[color:var(--prog-card-border-color)] bg-[var(--prog-tips-card-bg)] overflow-hidden ${className}`}
  >
    {children}
  </div>
);

const DetailCardHeading = ({ children }: { children: React.ReactNode }) => (
  <div className="px-6 pt-4 pb-[17px] border-b border-[color:var(--prog-card-border-color)]">
    <p className="text-sm font-semibold text-[color:var(--prog-text-heading)] tracking-[-0.15px]">
      {children}
    </p>
  </div>
);

// ── Sidebar meta row ───────────────────────────────────────────────────────────

const MetaRow = ({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) => (
  <div className="flex items-center gap-3">
    <span className="flex-shrink-0 flex items-center justify-center w-7 h-7 rounded-[10px] bg-[var(--prog-tips-card-bg)] text-[color:var(--prog-text-muted)]">
      {icon}
    </span>
    <div className="min-w-0">
      <p className="text-[10px] tracking-[0.12px] text-[color:var(--prog-text-muted)]">{label}</p>
      <p className="text-xs font-semibold leading-4 text-[color:var(--prog-text-heading)]">
        {value}
      </p>
    </div>
  </div>
);

// ── Feature list row ───────────────────────────────────────────────────────────

const FeatureRow = ({ children }: { children: React.ReactNode }) => (
  <div className="flex items-start gap-2">
    <Shield className="flex-shrink-0 mt-0.5 w-3 h-3 text-[#818CF8]" />
    <span className="text-xs text-[color:var(--prog-text-muted)]">{children}</span>
  </div>
);

// ── Loading skeleton ───────────────────────────────────────────────────────────

const Skeleton = ({ className = "" }: { className?: string }) => (
  <div className={`animate-pulse rounded-xl bg-[var(--prog-progress-bg)] ${className}`} />
);

const LoadingSkeleton = () => (
  <div className="flex gap-6 items-start">
    <div className="flex-1 flex flex-col gap-5">
      <div className="rounded-2xl border p-6 flex flex-col gap-4 bg-[var(--prog-card-bg)] border-[color:var(--prog-card-border)]">
        <div className="flex gap-2">
          <Skeleton className="h-6 w-28" />
          <Skeleton className="h-6 w-24" />
        </div>
        <Skeleton className="h-8 w-3/4" />
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
      <div className="rounded-2xl border p-6 flex flex-col gap-3 bg-[var(--prog-card-bg)] border-[color:var(--prog-card-border)]">
        <Skeleton className="h-5 w-36" />
        <Skeleton className="h-12 w-full" />
      </div>
    </div>
    <div className="w-72 flex-shrink-0 flex flex-col gap-4">
      <div className="rounded-2xl border p-6 flex flex-col gap-3 bg-[var(--prog-card-bg)] border-[color:var(--prog-card-border)]">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="h-5 w-full" />
        ))}
        <Skeleton className="mt-2 h-11 w-full rounded-2xl" />
      </div>
    </div>
  </div>
);

// ── Helpers ────────────────────────────────────────────────────────────────────

function sessionTypeLabel(type: "GROUP" | "ONE_ON_ONE" | null): string {
  if (type === "GROUP") return "Group session";
  if (type === "ONE_ON_ONE") return "1-on-1";
  return "Video call";
}

function sessionDurationMinutes(start: string | null, end: string | null): number | null {
  if (!start || !end) return null;
  const mins = differenceInMinutes(parseISO(end), parseISO(start));
  return mins > 0 ? mins : null;
}

// ── Detail sections (everything below the hero) ───────────────────────────────
// Extracted verbatim from the page body so the landing layout can place it
// inside the left column, beside the meta sidebar, instead of in a second row
// underneath it.

/**
 * One of a Peer Advisory program's ordered lists, rendered as a bulleted card
 * body. Prerequisites, outcomes, example challenges and the group agreement all
 * share this shape, so they share one renderer.
 *
 * Text goes through FormattedText because these fields accept markdown -- the
 * facilitator's editor converts pasted rich text to it.
 */
const PeerBulletList = ({
  items,
  icon: Icon,
  iconColor = "var(--prog-status-live-text)",
}: {
  items: { id: string | null; text: string }[];
  icon: LucideIcon;
  /**
   * Per-list accent. Defaults to the confirmation green, which is right for the
   * two lists that are assertions about the reader — Prerequisites ("you meet
   * these") and Group agreement ("you accept these"). The two that describe the
   * program itself get their own colours, so the page is not four identical
   * green ticklists.
   */
  iconColor?: string;
}) => (
  <ul className="flex flex-col gap-3 px-6 py-5">
    {items.map((item, index) => (
      <li key={item.id ?? `${item.text}-${index}`} className="flex items-start gap-3">
        <Icon className="mt-[3px] h-3.5 w-3.5 flex-shrink-0" style={{ color: iconColor }} />
        <span className="text-sm leading-[22.75px] tracking-[-0.15px] text-[color:var(--prog-text-muted)]">
          <FormattedText text={item.text} />
        </span>
      </li>
    ))}
  </ul>
);

const ProgramLandingSections = ({
  program,
  sessionsList,
}: {
  program: PublicProgramPreviewOut;
  sessionsList: PublicProgramPreviewOut["sessions"];
}) => (
  <>
    {/* Who this programme is for */}
    {program.whoIsThisProgramFor && (
      <DetailCard>
        <DetailCardHeading>Who this programme is for</DetailCardHeading>
        <p className="px-6 py-5 text-sm leading-[22.75px] tracking-[-0.15px] text-[color:var(--prog-text-muted)]">
          <FormattedText text={program.whoIsThisProgramFor} />
        </p>
      </DetailCard>
    )}

    {/* Prerequisites.
        Two shapes for the same idea: an Expert Program stores prose, a peer
        group an ordered list whose entries the applicant confirms one by one on
        their application. Rendered as a real list there so the page shows what
        the form will actually ask. */}
    {program.prerequisites && (
      <DetailCard>
        <DetailCardHeading>Prerequisites</DetailCardHeading>
        <p className="px-6 py-5 text-sm leading-[22.75px] tracking-[-0.15px] text-[color:var(--prog-text-muted)]">
          <FormattedText text={program.prerequisites} />
        </p>
      </DetailCard>
    )}

    {program.peer && program.peer.prerequisites.length > 0 && (
      <DetailCard>
        <DetailCardHeading>Prerequisites</DetailCardHeading>
        <PeerBulletList items={program.peer.prerequisites} icon={CheckCircle2} />
      </DetailCard>
    )}

    {/* What you'll achieve */}
    {program.programOutcomes && (
      <DetailCard>
        <DetailCardHeading>What you&apos;ll achieve</DetailCardHeading>
        <p className="px-6 py-5 text-sm leading-[22.75px] tracking-[-0.15px] text-[color:var(--prog-text-muted)]">
          <FormattedText text={program.programOutcomes} />
        </p>
      </DetailCard>
    )}

    {program.peer && program.peer.expectedOutcomes.length > 0 && (
      <DetailCard>
        <DetailCardHeading>What you&apos;ll achieve</DetailCardHeading>
        <PeerBulletList
          items={program.peer.expectedOutcomes}
          icon={Target}
          iconColor="var(--peer-detail-outcome-icon)"
        />
      </DetailCard>
    )}

    {/* What the group works through. No Expert equivalent: it exists because a
        peer group's value is the problems in the room, so an applicant needs to
        recognise their own situation before applying. */}
    {program.peer && program.peer.exampleChallenges.length > 0 && (
      <DetailCard>
        <DetailCardHeading>Challenges this group works through</DetailCardHeading>
        <PeerBulletList
          items={program.peer.exampleChallenges}
          icon={MessageCircle}
          iconColor="var(--peer-detail-challenge-icon)"
        />
      </DetailCard>
    )}

    {/* The ground rules, shown BEFORE applying rather than only inside the
        form: they are the terms of joining, and someone deciding whether to
        apply should be able to read them first. */}
    {program.peer && program.peer.groupAgreement.length > 0 && (
      <DetailCard>
        <DetailCardHeading>Group agreement</DetailCardHeading>
        <PeerBulletList items={program.peer.groupAgreement} icon={ShieldCheck} />
      </DetailCard>
    )}

    {/* Programme Goals */}
    {program.goals.length > 0 && (
      <DetailCard>
        <DetailCardHeading>Programme Goals</DetailCardHeading>
        <div className="px-6 py-5 flex flex-col gap-4">
          {program.goals.map((goal) => (
            <div key={goal.id} className="flex items-start gap-4">
              <span
                className="mt-0.5 flex-shrink-0 flex items-center justify-center rounded-full w-8 h-8"
                style={{ backgroundImage: "var(--prog-detail-tab-active-bg)" }}
              >
                <Target className="w-3.5 h-3.5 text-white" />
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold leading-5 tracking-[-0.15px] text-[color:var(--prog-text-heading)]">
                  <FormattedText text={goal.title} />
                </p>
                <p className="text-xs mt-1 text-[color:var(--prog-text-muted)]">
                  {goal.tasksCount} task{goal.tasksCount === 1 ? "" : "s"}
                </p>
              </div>
            </div>
          ))}
        </div>
      </DetailCard>
    )}

    {/* Session structure */}
    {sessionsList.length > 0 && (
      <DetailCard>
        <DetailCardHeading>Session Structure</DetailCardHeading>
        <div className="px-6 py-5 flex flex-col gap-3">
          {sessionsList.map((session, i) => {
            const durationMins = sessionDurationMinutes(session.startTime, session.endTime);
            return (
              <div
                key={session.id}
                className="flex items-start gap-4 rounded-[14px] border border-[color:var(--prog-card-border-color)] bg-[var(--prog-tips-card-bg)] p-[17px]"
              >
                <span className="flex-shrink-0 flex items-center justify-center rounded-[10px] w-8 h-8 bg-[rgba(64,81,181,0.2)]">
                  <CalendarDays className="w-3.5 h-3.5 text-[color:var(--prog-text-muted)]" />
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold leading-5 tracking-[-0.15px] text-[color:var(--prog-text-heading)]">
                    Session {i + 1} — {session.title}
                  </p>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="inline-flex items-center gap-1 text-xs text-[color:var(--prog-text-muted)]">
                      <Video className="w-2.5 h-2.5" />
                      {sessionTypeLabel(session.sessionType)}
                    </span>
                    {durationMins && (
                      <>
                        <span className="text-xs text-[color:var(--prog-text-muted)]">·</span>
                        <span className="inline-flex items-center gap-1 text-xs text-[color:var(--prog-text-muted)]">
                          <Clock className="w-2.5 h-2.5" />
                          {durationMins} min
                        </span>
                      </>
                    )}
                    {session.startTime && (
                      <>
                        <span className="text-xs text-[color:var(--prog-text-muted)]">·</span>
                        <span className="text-xs text-[color:var(--prog-text-muted)]">
                          {formatDisplayDate(session.startTime, { length: "long" })}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </DetailCard>
    )}
  </>
);

// ── Reusable landing content ───────────────────────────────────────────────────

export interface ProgramLandingContentProps {
  program: PublicProgramPreviewOut;
  onPrimaryAction: () => void;
  /** Overrides the hero button's label — e.g. "Enrolling…" mid-request. */
  primaryActionLabel?: string;
  /** Disables the hero button while an enrollment request is in flight. */
  primaryActionDisabled?: boolean;
  /**
   * The viewer is already enrolled. Both action slots (hero and sidebar)
   * become green status chips: same size, full opacity, no click target.
   * See FeaturedCard's actionSubscribed for why this isn't just `disabled`.
   */
  primaryActionSubscribed?: boolean;
  /** Hides the hero button entirely — see FeaturedCard's hideAction. */
  hidePrimaryAction?: boolean;
}

export const ProgramLandingContent = ({
  program,
  onPrimaryAction,
  primaryActionLabel = "Enroll in Program",
  primaryActionDisabled = false,
  primaryActionSubscribed = false,
  hidePrimaryAction = false,
}: ProgramLandingContentProps) => {
  const enrolledCount = program.membersCount ?? (program.achievers ?? []).length;
  const totalSlots = program.maxSlots ?? enrolledCount + (program.slotsLeft ?? 0);

  const startDate =
    formatDisplayDate(program.startsAt, { variant: "date", length: "long" }) || null;

  const okrsCount = program.okrsCount ?? (program.goals ?? []).length;
  const sessionsCount = program.sessionsCount ?? (program.sessions ?? []).length;
  const sessionsList = program.sessions ?? [];

  return (
    /* One two-column row. The hero and the sidebar used to form their own
       row, which made that row as tall as the sidebar (~890px) while
       FeaturedCard is locked at 420px -- so the sections below started under
       the *sidebar*, leaving a block of dead space beside the hero. The left
       column now stacks the hero and the sections, so they flow up alongside
       the taller sidebar. */
    <div className="flex gap-6 items-start">
      {/* ── Left column: hero, then every detail section ──────────────── */}
      <div className="flex-1 min-w-0 flex flex-col gap-5">
        {/* Hero card — same FeaturedCard used on the Explore page, so a
              program looks identical whether discovered there or opened
              directly via its public link. Always shows "Expert Program"; the
              "Featured" ribbon only appears when program.isFeatured is true. */}
        <FeaturedCard
          program={program}
          actionLabel={primaryActionLabel}
          actionDisabled={primaryActionDisabled}
          actionSubscribed={primaryActionSubscribed}
          hideAction={hidePrimaryAction}
          onAction={onPrimaryAction}
        />
        <ProgramLandingSections program={program} sessionsList={sessionsList} />
      </div>

      {/* ── Right column: meta + primary action card (Figma node 177:3702).
          Sizes to its own content — no h-full, since there's no stretched
          parent to fill and the left column scrolls past it. ───────────── */}
      <div className="w-72 flex-shrink-0">
        <DetailCard>
          <div className="p-5">
            <div className="flex flex-col gap-3">
              {startDate && (
                <MetaRow
                  icon={<CalendarDays className="w-3.5 h-3.5" />}
                  label="Start Date"
                  value={startDate}
                />
              )}
              {formatProgramDuration(publicDurationFields(program)) && (
                <MetaRow
                  icon={<Clock className="w-3.5 h-3.5" />}
                  label="Duration"
                  value={formatProgramDuration(publicDurationFields(program))!}
                />
              )}
              {program.weeklyTimeCommitment && (
                <MetaRow
                  icon={<Clock className="w-3.5 h-3.5" />}
                  label="Weekly Time"
                  value={program.weeklyTimeCommitment}
                />
              )}
              {okrsCount > 0 && (
                <MetaRow
                  icon={<Target className="w-3.5 h-3.5" />}
                  label="Goals"
                  value={`${okrsCount} ${okrsCount === 1 ? "goal" : "goals"}`}
                />
              )}
              {/* A peer group's numbers answer different questions: how big the
                  room will be, and when applications shut. "Spots Left" would
                  be misleading -- the seats are not first-come, a facilitator
                  reviews and selects. */}
              {program.peer ? (
                <>
                  {program.peer.minMembers != null && program.peer.maxMembers != null && (
                    <MetaRow
                      icon={<Users className="w-3.5 h-3.5" />}
                      label="Group Size"
                      value={`${program.peer.minMembers}–${program.peer.maxMembers} members`}
                    />
                  )}
                  <MetaRow
                    icon={<CalendarDays className="w-3.5 h-3.5" />}
                    label="Applications Close"
                    value={formatDisplayDate(program.peer.applicationDeadline, {
                      variant: "date",
                      length: "long",
                      fallback: "Open",
                    })}
                  />
                  <MetaRow
                    icon={<Shield className="w-3.5 h-3.5" />}
                    label="Joining"
                    value="Reviewed by the facilitator"
                  />
                </>
              ) : (
                <MetaRow
                  icon={<Users className="w-3.5 h-3.5" />}
                  label="Spots Left"
                  value={
                    totalSlots > 0
                      ? `${program.slotsLeft} of ${totalSlots}`
                      : `${program.slotsLeft} spots`
                  }
                />
              )}
            </div>

            {/* Primary action button — Figma node 177:3766 */}
            {!hidePrimaryAction && (
              <div className="mt-4 pt-4 border-t border-[color:var(--prog-card-border-color)]">
                {primaryActionSubscribed ? (
                  // A green status chip, not a dimmed button. This card follows
                  // the page theme (unlike the hero, which is always dark), so
                  // it uses the theme's own success tokens -- they already
                  // define a light pair (#008236 on #DCFCE7) and a dark pair
                  // (#34D399 on translucent emerald), so both themes are
                  // legible without a dark: variant here.
                  <span className="flex h-12 w-full items-center justify-center gap-2 rounded-[14px] border border-theme-border-success bg-theme-bg-success text-sm font-bold tracking-[-0.15px] text-theme-text-success">
                    <Check className="h-4 w-4" aria-hidden="true" />
                    {primaryActionLabel}
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={onPrimaryAction}
                    disabled={primaryActionDisabled}
                    className="w-full h-12 rounded-[14px] text-sm font-bold text-white tracking-[-0.15px] transition-opacity hover:opacity-90 disabled:opacity-60 disabled:pointer-events-none"
                    style={{ backgroundImage: "var(--prog-detail-tab-active-bg)" }}
                  >
                    {primaryActionLabel}
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Delivered by — same card, bottom section */}
          <div className="px-5 pb-5">
            <div className="pt-[17px] border-t border-[color:var(--prog-card-border-color)]">
              <p className="text-[10px] font-semibold uppercase tracking-[0.6px] text-[color:var(--prog-text-muted)]">
                Delivered by AchieveOS
              </p>
              <div className="mt-3 flex flex-col gap-2">
                <FeatureRow>
                  {program.sessionMode === "SINGLE"
                    ? "A focused single-session program"
                    : program.durationDays
                      ? `Structured ${program.durationDays}-day program`
                      : program.durationWeeks > 0
                        ? `Structured ${program.durationWeeks}-week journey`
                        : "Structured program journey"}
                </FeatureRow>
                {sessionsCount > 0 && (
                  <FeatureRow>
                    {sessionsCount} live session{sessionsCount === 1 ? "" : "s"}
                  </FeatureRow>
                )}
                <FeatureRow>Live group sessions</FeatureRow>
                <FeatureRow>Goal tracking &amp; accountability</FeatureRow>
                {/* Who you'll actually be in the room with. A peer group is not
                    expert-led — it is peers advising each other with a
                    facilitator holding the room — so promising "Lead Expert +
                    Co-Experts" here would describe the wrong product. */}
                <FeatureRow>
                  {program.peer ? "Peer group + facilitator" : "Lead Expert + Co-Experts"}
                </FeatureRow>
              </div>
            </div>
          </div>
        </DetailCard>
      </div>
    </div>
  );
};

// ── Main page ──────────────────────────────────────────────────────────────────

/**
 * The public program page's own header: the logo (home), and for a visitor
 * Log in plus a primary action, or a way back into the app when signed in.
 * Sticky, and aligned to the page's content column.
 */
const PublicProgramHeader = ({
  isSignedIn,
  onPrimaryAction,
  primaryLabel,
}: {
  isSignedIn: boolean;
  onPrimaryAction?: () => void;
  primaryLabel: string;
}) => {
  const navigate = useNavigate();
  return (
    <header className="sticky top-0 z-20 border-b border-theme-border bg-theme-bg-header">
      <div className="mx-auto flex h-[72px] w-full max-w-6xl items-center justify-between gap-4 px-6">
        <button
          type="button"
          onClick={() => navigate("/")}
          aria-label="AchieveOS home"
          className="flex-shrink-0"
        >
          <img
            src={achieveosLogo}
            alt="AchieveOS"
            className="h-[34px] w-[146px] object-contain object-left dark:hidden"
          />
          <img
            src={achieveosLogoDark}
            alt="AchieveOS"
            className="hidden h-[34px] w-[146px] object-contain object-left dark:block"
          />
        </button>
        <div className="flex items-center gap-2 sm:gap-3">
          <ThemeToggle />
          {isSignedIn ? (
            <button
              type="button"
              onClick={() => navigate("/home")}
              className="inline-flex h-10 items-center rounded-[10px] bg-primary-purple px-4 text-[14px] font-semibold text-white transition-opacity hover:opacity-90"
            >
              Go to AchieveOS
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={() => navigate("/login")}
                className="inline-flex h-10 items-center rounded-[10px] border border-theme-border bg-theme-bg-surface px-4 text-[14px] font-semibold text-theme-text-heading transition-colors hover:bg-theme-bg-hover"
              >
                Log in
              </button>
              {onPrimaryAction && (
                <button
                  type="button"
                  onClick={onPrimaryAction}
                  className="hidden h-10 items-center rounded-[10px] bg-primary-purple px-4 text-[14px] font-semibold text-white transition-opacity hover:opacity-90 sm:inline-flex"
                >
                  {primaryLabel}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </header>
  );
};

export const PublicProgramLandingPage = () => {
  const { programId } = useParams<{ programId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const isPreview = searchParams.get("preview") === "true";
  const role = searchParams.get("role") ?? "achiever";
  // No default org code. A visitor arriving without one has no company, which
  // is what makes them a paying (self-serve) enrollee. Only a genuine code
  // passed in the URL — a real sponsoring company — is honoured.
  const code = searchParams.get("code") ?? "";

  const {
    data: program,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["public-program-preview", programId],
    queryFn: () => publicApi.programs.getPreview(programId!),
    enabled: !!programId,
    retry: false,
  });

  const handlePrimaryAction = () => {
    tracking.publicProgramEnrollClicked({ program_id: programId ?? "", role, signup_code: code });

    // Already signed in THIS tab? Go straight to the in-app program page.
    // Auth is per-tab (sessionStorage `fwd_token`), so this only fires when
    // the link is opened in a tab that is already signed in. A link opened in
    // a NEW tab has no token, `user` is null, and the signup path below runs
    // exactly as before -- correct, since we genuinely cannot tell whether
    // that visitor has an account.
    if (user) {
      navigate(`/explore/${programId}`);
      return;
    }

    // Only append &code= when one was actually supplied; an empty param would
    // fail org-code validation on signup.
    const codeParam = code ? `&code=${encodeURIComponent(code)}` : "";
    navigate(`/signup?role=${role}&join=${programId}${codeParam}`);
  };

  if (isError) {
    return (
      <div className="min-h-screen flex flex-col bg-[var(--prog-page-bg)]">
        <PublicProgramHeader isSignedIn={!!user} primaryLabel="Join AchieveOS" />
        <div className="flex flex-1 items-center justify-center px-6">
          <div className="text-center">
            <p className="text-base font-semibold text-[color:var(--prog-text-heading)]">
              Program not found
            </p>
            <p className="mt-1 text-sm text-[color:var(--prog-text-muted)]">
              This program may no longer be available.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-[var(--prog-page-bg)]">
      {/* ── Header ───────────────────────────────────────────────────── */}
      <PublicProgramHeader
        isSignedIn={!!user}
        primaryLabel="Join AchieveOS"
        // In preview the expert is looking at their own page; no signup CTA.
        onPrimaryAction={isPreview ? undefined : handlePrimaryAction}
      />

      {/* ── Preview bar: only when the expert previews their own program ── */}
      {isPreview && (
        <div className="border-b px-8 py-3 flex items-center justify-between bg-[var(--prog-subheader-bg)] border-[color:var(--prog-subheader-border)]">
          <span className="text-sm font-medium text-[color:var(--prog-text-muted)]">
            Preview Mode
          </span>
          <div className="flex items-center gap-3">
            <Badge className="bg-[var(--prog-preview-duration-bg)] text-[color:var(--prog-preview-duration-text)]">
              Preview
            </Badge>
            {program && (program.status === "DRAFT" || program.status === "ACTIVE") && (
              <Badge className="bg-[var(--prog-status-enroll-bg)] text-[color:var(--prog-status-enroll-text)]">
                Open for Enrollment
              </Badge>
            )}
          </div>
        </div>
      )}

      {/* ── Page content ─────────────────────────────────────────────── */}
      <div className={`flex-1 w-full max-w-6xl mx-auto px-6 py-8 ${isPreview ? "pb-24" : ""}`}>
        {isLoading ? (
          <LoadingSkeleton />
        ) : program ? (
          <ProgramLandingContent program={program} onPrimaryAction={handlePrimaryAction} />
        ) : null}
      </div>

      {/* ── Preview bottom bar — only visible in preview mode ────────── */}
      {isPreview && (
        <div
          className="fixed bottom-0 left-0 right-0 z-20 flex h-[77px] items-center justify-end px-9 border-t border-[rgba(255,255,255,0.08)]"
          style={{ background: "rgba(12,18,32,0.97)" }}
        >
          <Button
            variant="primaryGradient"
            size="sm"
            className="rounded-xl px-5 py-2.5 text-[13px] font-semibold"
            onClick={() => navigate(-1)}
          >
            Back to Program Details
          </Button>
        </div>
      )}
    </div>
  );
};
